/* eslint-disable max-len */
import { Table, type TableProps } from 'antd';
import { type Dispatch, type Key, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { type ConsentsAndDeclarationsListDataFragment } from '../../../../api/fragments/ConsentsAndDeclarationsListData';
import { type ConsentsAndDeclarationsModuleSpecsFragment } from '../../../../api/fragments/ConsentsAndDeclarationsModuleSpecs';
import PaginatedTable, { type PageAction } from '../../../../components/PaginatedTable';
import renderBooleanIcon from '../../../../utilities/renderBooleanIcon';
import { type State } from '../../ConsentsAndDeclarationsListPage/useListReducer';

export type ConsentsAndDeclarationsModuleTableProps = {
    module: ConsentsAndDeclarationsModuleSpecsFragment;
    dataSource: ConsentsAndDeclarationsListDataFragment[];
    state: State;
    dispatch: Dispatch<PageAction>;
    total: number;
};

type ModuleDataSource = ConsentsAndDeclarationsListDataFragment & { key: Key };
type ConsentsTable = TableProps<ModuleDataSource>;

const ConsentsAndDeclarationsModuleTable = ({
    module,
    dataSource,
    dispatch,
    state,
    total,
}: ConsentsAndDeclarationsModuleTableProps) => {
    const { t } = useTranslation(['consentsAndDeclarationsModuleDetails', 'consentsAndDeclarations']);
    const navigate = useNavigate();

    const onRow = useCallback<ConsentsTable['onRow']>(
        record => ({
            onClick: () => navigate(`/admin/consents/${record.id}`),
        }),
        [navigate]
    );

    return (
        <PaginatedTable
            dataSource={dataSource}
            dispatch={dispatch}
            onRow={onRow}
            rowKey="id"
            state={state}
            tableName={t('consentsAndDeclarations:title')}
            total={total}
        >
            <Table.Column
                key="displayName"
                dataIndex="displayName"
                title={t('consentsAndDeclarationsModuleDetails:consentsModuleTable.columns.displayName')}
            />
            <Table.Column
                key="type"
                align="center"
                dataIndex="type"
                render={value => renderBooleanIcon(value, true)}
                title={t('consentsAndDeclarationsModuleDetails:consentsModuleTable.columns.type')}
                width={120}
            />
            <Table.Column
                key="isMandatory"
                align="center"
                dataIndex="isMandatory"
                render={value => renderBooleanIcon(value, true)}
                title={t('consentsAndDeclarationsModuleDetails:consentsModuleTable.columns.isMandatory')}
                width={120}
            />
            <Table.Column
                key="isActive"
                align="center"
                dataIndex="isActive"
                render={value => renderBooleanIcon(value, true)}
                title={t('consentsAndDeclarationsModuleDetails:consentsModuleTable.columns.isActive')}
                width={120}
            />
        </PaginatedTable>
    );
};

export default ConsentsAndDeclarationsModuleTable;
